import { useEffect, useRef, useState } from "react";
import { toIdeaMode, type IdeaMode, type IdeaRunsOn } from "@mycompbox/shared";
import { cloudReasonText } from "@/components/epic-requests/run-with-choice";
import { useCloudRunStatus, useStartCloudRun } from "@/hooks/queries/use-cloud-runs";
import { useEnqueueJob, useMachines } from "@/hooks/queries/use-mailbox";
import { useMe } from "@/hooks/queries/use-me";
import { ApiError } from "@/lib/api/client";
import { machineReadiness } from "@/lib/api/mailbox";
import { cn } from "@/lib/utils";

/**
 * How an idea moves after you approve it.
 *
 * - Manual (the default): you approve, make the blueprint, start the build and
 *   merge the pull requests yourself. Each person can make Autopilot their
 *   default in Settings (User.ideaModeDefault), and switch any one idea here.
 * - Autopilot: you shape and approve the idea by hand; from then on the
 *   blueprint, the build and merging its pull requests (once checks pass) all
 *   happen by themselves. The server starts the build when the blueprint is
 *   done (jobService enqueueAutopilotBuild) and lets that build merge, so it
 *   carries on with this page closed.
 *
 * Both follow where the idea runs (ER-145): on a computer, or in CompBox cloud.
 */

export type { IdeaMode };

const MODE_KEY = "compbox-idea-mode";

/** This idea's own pick on this browser, or null when it follows the person's default. */
function readMode(ideaId: string): IdeaMode | null {
  try {
    const stored = window.localStorage.getItem(`${MODE_KEY}:${ideaId}`);
    return stored === "manual" || stored === "autopilot" ? stored : null;
  } catch {
    return null;
  }
}

/**
 * The idea's mode: its own pick, else the person's default from Settings
 * (Manual until it loads, so nothing starts by itself on a guess).
 */
export function useIdeaMode(ideaId: string) {
  const { data: me } = useMe();
  const [picked, setPicked] = useState<IdeaMode | null>(() => readMode(ideaId));
  useEffect(() => {
    setPicked(readMode(ideaId));
  }, [ideaId]);
  const set = (next: IdeaMode) => {
    try {
      window.localStorage.setItem(`${MODE_KEY}:${ideaId}`, next);
    } catch {
      // Private window or blocked storage: the choice still holds for this visit.
    }
    setPicked(next);
  };
  return [picked ?? toIdeaMode(me?.ideaModeDefault), set] as const;
}

export type AutoStep =
  | { kind: "plan"; epicRequestId: string }
  | { kind: "orchestrate"; epicId: string; fastMode: boolean }
  | null;

/**
 * In Autopilot, starts `step` (an approved idea's blueprint, or a waiting
 * blueprint's build) once, marked Autopilot so the server carries on from
 * there: on your first ready computer, or as a CompBox cloud run when the idea
 * runs in the cloud. A cloud run that can't start says why; it never falls
 * back to a computer. Returns a short line about what happened.
 */
export function useAutoRun(
  mode: IdeaMode,
  step: AutoStep,
  onStarted: () => void,
  runsOn: IdeaRunsOn | null = null
): string | null {
  const { data: machines } = useMachines();
  const enqueue = useEnqueueJob();
  const startCloud = useStartCloudRun();
  const started = useRef<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const machine = (machines ?? []).find((m) => machineReadiness(m) === "ready") ?? null;
  const stepKey = step ? (step.kind === "plan" ? `plan:${step.epicRequestId}` : `build:${step.epicId}`) : null;
  const inCloud = runsOn === "cloud";
  const target = step ? (step.kind === "plan" ? { epicRequestId: step.epicRequestId } : { epicId: step.epicId }) : {};
  const { data: cloudStatus } = useCloudRunStatus(target, mode === "autopilot" && inCloud && !!step);
  const cloudReason = cloudStatus ? cloudReasonText(cloudStatus) : null;

  useEffect(() => {
    if (mode !== "autopilot" || !step || !stepKey) return;
    if (started.current === stepKey) return;
    const what = step.kind === "plan" ? "blueprint" : "build";

    if (inCloud) {
      if (!cloudStatus) return;
      if (cloudReason) {
        setNote(`Autopilot can't run the ${what} in CompBox cloud: ${cloudReason}.`);
        return;
      }
      started.current = stepKey;
      setNote(null);
      startCloud
        .mutateAsync(
          step.kind === "plan"
            ? { kind: "plan", epicRequestId: step.epicRequestId, autopilot: true }
            : { kind: "orchestrate", epicId: step.epicId, fastMode: step.fastMode, autopilot: true }
        )
        .then(() => {
          setNote(`Autopilot started the ${what} in CompBox cloud.`);
          onStarted();
        })
        .catch((err: unknown) => {
          started.current = null;
          setNote(err instanceof ApiError ? err.message : "Autopilot couldn't start the next step. Try again.");
        });
      return;
    }

    if (!machine) {
      setNote("Autopilot is waiting for one of your computers. Open CompBox on it.");
      return;
    }
    started.current = stepKey;
    setNote(null);
    enqueue
      .mutateAsync({ machineId: machine.id, type: "start_execution", payload: { ...step, autopilot: true } })
      .then(() => {
        setNote(`Autopilot started the ${what} on ${machine.name}.`);
        onStarted();
      })
      .catch((err: unknown) => {
        started.current = null;
        setNote(err instanceof ApiError ? err.message : "Autopilot couldn't start the next step. Try again.");
      });
    // Start once per step; the other values are read fresh when it fires.
  }, [mode, stepKey, machine?.id, inCloud, cloudStatus?.available, cloudReason]);

  return note;
}

const OPTIONS: { value: IdeaMode; label: string; help: string }[] = [
  {
    value: "manual",
    label: "Manual",
    help: "You approve, make the blueprint, start the build and merge the PRs yourself.",
  },
  {
    value: "autopilot",
    label: "Autopilot",
    help: "After you approve: blueprint, build and merging the PRs when checks pass, all by itself.",
  },
];

const RUNS_ON_OPTIONS: { value: IdeaRunsOn; label: string }[] = [
  { value: "computer", label: "My computer" },
  { value: "cloud", label: "CompBox cloud" },
];

export function AutomationBar({
  mode,
  onMode,
  note,
  runsOn,
  onRunsOn,
}: {
  mode: IdeaMode;
  onMode: (mode: IdeaMode) => void;
  note: string | null;
  /** Where the idea runs (ER-145). The picker shows only with `onRunsOn` (the idea's owner). */
  runsOn?: IdeaRunsOn | null;
  onRunsOn?: (runsOn: IdeaRunsOn) => void;
}) {
  const current = OPTIONS.find((o) => o.value === mode) ?? OPTIONS[0];
  return (
    <section aria-label="Mode" className="flex flex-col gap-2 rounded-2xl border border-border bg-background px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div role="radiogroup" aria-label="Mode" className="flex flex-wrap items-center gap-2">
          {OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={mode === o.value}
              onClick={() => {
                onMode(o.value);
              }}
              className={cn(
                "inline-flex min-h-11 items-center rounded-xl border px-4 text-sm font-semibold",
                mode === o.value
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-foreground hover:bg-muted"
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
        {onRunsOn && (
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            Where this idea runs
            <select
              aria-label="Where this idea runs"
              value={runsOn ?? "computer"}
              onChange={(e) => {
                onRunsOn(e.target.value === "cloud" ? "cloud" : "computer");
              }}
              className="min-h-11 rounded-xl border border-border bg-background px-3 text-sm text-foreground"
            >
              {RUNS_ON_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <p className="text-xs text-muted-foreground">{current?.help}</p>
      {note && (
        <p role="status" className="text-xs font-medium text-foreground">
          {note}
        </p>
      )}
    </section>
  );
}
